import { Drawer } from '@/components';
import { getInitialExpenditureBudgetPurposeIds } from '@/features/mold/utils/formatter/fund-formatter';
import {
  useProject,
  useUpdateFundAdditionalAttributes,
} from '@/features/project/_hooks/project';
import { isBefore2025 } from '@/features/project/_utils/project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { boolToString } from '@/utils';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FundAdditionalAttributesModel } from '../../../_models/project';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import {
  FundAdditionalAttributesForm,
  emptyCompositionBackground,
} from './FundAdditionalAttributesForm';

export const FundAdditionalAttributeFormsDrawer = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();

  const { id: projectId } = useId();
  const project = useProject(projectId);
  const { mutateAsync: updateFundAdditionalAttributes } =
    useUpdateFundAdditionalAttributes();

  const isKsLocalGovernmentAfter2025 = useMemo(
    () =>
      !isBefore2025(project.data) &&
      project.data?.fundAdditionalAttributes?.fundSheetType ===
        'local-government',
    [project.data],
  );

  if (project.isPending || !project.data) {
    return null;
  }

  const parsedFundAdditionalAttributes =
    FundAdditionalAttributesModel.safeParse(
      project.data.fundAdditionalAttributes,
    );

  if (!parsedFundAdditionalAttributes.success) {
    return null;
  }

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  return (
    <Drawer
      size="large"
      open={
        drawerState.open && drawerState.type === 'fund-additional-attributes'
      }
      onClose={handleClose}
      destroyOnClose
    >
      <FundAdditionalAttributesForm
        fundSheetType={project.data?.fundAdditionalAttributes?.fundSheetType}
        fiscalYear={project.data.fiscalYear}
        initialValues={{
          ...parsedFundAdditionalAttributes.data,
          compositionBackgrounds:
            isKsLocalGovernmentAfter2025 &&
            (!parsedFundAdditionalAttributes.data.compositionBackgrounds ||
              parsedFundAdditionalAttributes.data.compositionBackgrounds
                .length === 0)
              ? [emptyCompositionBackground]
              : parsedFundAdditionalAttributes.data.compositionBackgrounds?.map(
                  ({ expenditureBudgetPurposes, ...background }) => {
                    return {
                      ...background,
                      expenditureBudgetPurposeIds:
                        getInitialExpenditureBudgetPurposeIds(
                          expenditureBudgetPurposes,
                        ),
                      haveAppliedLawOfAppropriatenessOfSubsidies: boolToString(
                        background.haveAppliedLawOfAppropriatenessOfSubsidies,
                      ),
                      fiscalYearOfBudgetaryProvision:
                        background.fiscalYearOfBudgetaryProvision
                          ? dayjs().year(
                              background.fiscalYearOfBudgetaryProvision,
                            )
                          : undefined,
                      fiscalYearOfCreation: background.fiscalYearOfCreation
                        ? dayjs().year(background.fiscalYearOfCreation)
                        : undefined,
                      relatedReviewSheetYear: background.relatedReviewSheetYear
                        ? dayjs().year(background.relatedReviewSheetYear)
                        : undefined,
                    };
                  },
                ),
          nationalTreasuryReturnBackgrounds:
            parsedFundAdditionalAttributes.data.nationalTreasuryReturnBackgrounds?.map(
              (natioanlTreasury) => {
                return {
                  ...natioanlTreasury,
                  fiscalYear: natioanlTreasury.fiscalYear
                    ? dayjs().year(natioanlTreasury.fiscalYear)
                    : undefined,
                };
              },
            ),
          scheduledEnd: {
            ...parsedFundAdditionalAttributes.data.scheduledEnd,
            endDate: parsedFundAdditionalAttributes.data.scheduledEnd?.endDate
              ? dayjs(parsedFundAdditionalAttributes.data.scheduledEnd.endDate)
              : undefined,
            endDateOfNewApplicationReceiving: parsedFundAdditionalAttributes
              .data.scheduledEnd?.endDateOfNewApplicationReceiving
              ? dayjs(
                  parsedFundAdditionalAttributes.data.scheduledEnd
                    .endDateOfNewApplicationReceiving,
                )
              : undefined,
          },
        }}
        onSubmit={async (values) => {
          await updateFundAdditionalAttributes({
            id: projectId,
            ...values,
          });
          await Promise.all([
            openMessage('success', t('message-update-completed')),
            handleClose(),
          ]);
        }}
      />
    </Drawer>
  );
};
